import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { useDroppable } from "@dnd-kit/core";
import { Box, Tooltip, Typography } from "@mui/material";
import { IPrioritySchedulerColumnProps } from "../PrioritySchedulerBoard.types";
import { getTemplateWindowsByIdForCalendar } from "../PrioritySchedulerBoard.utils";
import { useEventLayouts } from "../useEventLayouts";
import { DraggableEventCard } from "./DraggableEventCard";
import { DraggingLineIndicator } from "./DraggingLineIndicator";

const STACK_GAP_PX = 1;

function PrioritySchedulerColumnComponent({
  EventComponent,
  id,
  columnDnDId,
  events = [],
  holiday,
  hourHeight,
  disabledSlots = [],
  dayStartTime,
  dayEndTime,
  date: columnDate,
  layoutReferenceDate,
  timelineContentHeight,
  dragIndicatorY,
  workTemplateBases,
  onEventRemove,
  onCardClick,
  registerColumnDroppableNode,
}: Readonly<IPrioritySchedulerColumnProps>) {
  const [hours, minutes] = dayStartTime?.split(":").map(Number) ?? [0, 0];
  const hoursToDayStartTime = hours + (minutes ?? 0) / 60;
  const topPosition = hourHeight ? hoursToDayStartTime * hourHeight : 0;

  const templateWindowsById = useMemo(
    () => getTemplateWindowsByIdForCalendar(workTemplateBases),
    [workTemplateBases]
  );

  const { layouts, maxBottom } = useEventLayouts({
    events,
    hourHeight,
    columnDate: columnDate ?? layoutReferenceDate,
    dayStartTime,
    dayEndTime,
    stackGapPx: STACK_GAP_PX,
    templateWindowsById,
  });

  const schedulableHeight = Math.max(
    timelineContentHeight - topPosition,
    maxBottom + STACK_GAP_PX * 2
  );

  const { classes } = useStyles({
    isHoliday: !!holiday,
    topPosition,
    schedulableHeight,
  });
  const droppableId = columnDnDId ?? `column:${id}`;
  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: "container",
      container: { type: "column", id },
    },
  });

  const setDropContainerRef = (node: HTMLDivElement | null) => {
    setDroppableNodeRef(node);
    registerColumnDroppableNode?.(id, node);
  };

  const sortedForPaint = useMemo(() => {
    return [...events].sort((a, b) => {
      const ta = layouts.get(a.id)?.top ?? 0;
      const tb = layouts.get(b.id)?.top ?? 0;
      return ta - tb;
    });
  }, [events, layouts]);

  return (
    <Box className={classes.columnContainer}>
      {!holiday &&
        disabledSlots.map((slot) => {
          const startDecimal = slot.start.hours() + slot.start.minutes() / 60;
          const endDecimal = slot.end.hours() + slot.end.minutes() / 60;
          const top = startDecimal * hourHeight;
          const height = (endDecimal - startDecimal) * hourHeight - 8;

          return (
            <Tooltip key={slot.id} title={slot.note ?? ""}>
              <Box style={{ top, height }} className={classes.disabledSlot}>
                {slot.note && (
                  <Typography className={classes.disabledText}>
                    {slot.note}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}

      {holiday && (
        <Box
          style={{
            top: 0,
            height: 24 * hourHeight - 8,
          }}
          className={classes.disabledSlot}
        >
          {holiday.name && (
            <Typography className={classes.disabledText}>
              {holiday.name}
            </Typography>
          )}
        </Box>
      )}

      <Box ref={setDropContainerRef} className={classes.cardContainer}>
        {dragIndicatorY != null && (
          <DraggingLineIndicator offsetY={dragIndicatorY} />
        )}
        {sortedForPaint.map((event) => {
          const layout = layouts.get(event.id) ?? {
            top: 0,
            height: hourHeight,
          };

          return (
            <DraggableEventCard
              key={event.id}
              event={event}
              layout={layout}
              columnId={id}
              onEventRemove={onEventRemove}
              onCardClick={onCardClick}
              EventComponent={EventComponent}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export const PrioritySchedulerColumn = memo(PrioritySchedulerColumnComponent);

const useStyles = makeStyles<{
  isHoliday?: boolean;
  topPosition: number;
  schedulableHeight: number;
}>()((theme, { isHoliday, topPosition, schedulableHeight }) => ({
  columnContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: isHoliday
      ? theme.palette.grey.lightAccent
      : theme.palette.info.light,
    padding: theme.spacing(0, 1),
    height: "100%",
    width: "100%",
    minHeight: 0,
    flex: 1,
    borderBottomRightRadius: theme.spacing(1),
    borderBottomLeftRadius: theme.spacing(1),
  },
  cardContainer: {
    position: "absolute",
    top: topPosition,
    left: 0,
    right: 0,
    width: "100%",
    padding: theme.spacing(0, 1),
    minHeight: schedulableHeight,
  },
  dropIndicatorLine: {
    position: "absolute",
    left: theme.spacing(1),
    right: theme.spacing(1),
    height: 2,
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    pointerEvents: "none",
  },
  disabledSlot: {
    position: "absolute",
    left: 0,
    right: 0,
    backgroundColor: `${theme.palette.grey.dark}14`,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 1.5),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.grey.medium}`,
    margin: theme.spacing(1),
    overflow: "hidden",
    backgroundImage: `repeating-linear-gradient(
    -160deg, 
    transparent, 
    transparent 30px, 
    ${theme.palette.grey.dark}10 30px, 
    ${theme.palette.grey.dark}10 40px
  )`,
  },
  disabledText: {
    color: theme.palette.grey.medium,
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.semiBold,
  },
  ghostEvent: {
    opacity: 0.5,
  },
  eventContainer: {
    width: "100%",
  },
}));
